import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, LockOpen, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { AButton, Chip, DataTable, Note, Panel, Section } from './Section'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog'
import { RateLimitHelpDesk } from './RateLimitHelpDesk'
import { cn } from '../../lib/cn'
import { api, get } from '../../lib/api'
import type { AdminAccount } from './types'

/**
 * Staff access — slide 23's "manage booth links & PINs, judges".
 *
 * Accounts use a username and password rather than the deck's per-booth link
 * and PIN (a Committee change), so what Admin needs here is to clear a
 * lockout and to issue a replacement password mid-event.
 */

const ROLE_LABEL = {
  admin: 'Admin',
  booth_manager: 'Booth Manager',
  judge: 'Judge',
} as const

export function AccountsSection() {
  const queryClient = useQueryClient()
  const [issued, setIssued] = useState<{ username: string; password: string } | null>(null)
  const [confirmReset, setConfirmReset] = useState<AdminAccount | null>(null)
  const [error, setError] = useState<string | null>(null)

  const accounts = useQuery({
    queryKey: ['admin-accounts'],
    queryFn: () => get<{ accounts: AdminAccount[] }>('/api/admin/accounts', 'staff'),
  })

  const unlock = useMutation({
    mutationFn: (id: number) =>
      api(`/api/admin/accounts/${id}/unlock`, { method: 'POST', auth: 'staff' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-accounts'] }),
    onError: () => setError('Could not clear that lockout.'),
  })

  const reset = useMutation({
    mutationFn: (id: number) =>
      api<{ username: string; password: string }>(`/api/admin/accounts/${id}/reset-password`, {
        method: 'POST',
        auth: 'staff',
      }),
    onSuccess: (res) => {
      setIssued(res)
      setConfirmReset(null)
      queryClient.invalidateQueries({ queryKey: ['admin-accounts'] })
    },
    onError: () => {
      setConfirmReset(null)
      setError('Could not reset that password.')
    },
  })

  const rows = accounts.data?.accounts ?? []
  const locked = rows.filter((a) => a.locked).length

  return (
    <Section
      title="Staff access"
      description={`${rows.length} accounts · ${rows.filter((a) => a.mustChangePassword).length} still on a temporary password`}
    >
      {error && (
        <Note tone="error" className="mb-3">
          {error}
        </Note>
      )}

      {locked > 0 && (
        <Note tone="attention" className="mb-3">
          <TriangleAlert className="mt-px size-4 shrink-0" strokeWidth={2.2} />
          <span>
            {locked} account{locked > 1 ? 's are' : ' is'} locked after five wrong passwords.
            Clearing the lockout gets them back in immediately.
          </span>
        </Note>
      )}

      {/*
        An account lockout and a rate limit look identical to the person who
        cannot sign in, but only one of them has an Unlock button. Without
        this, a tripped limit could only be cleared by restarting the server —
        not something to do mid-event with a queue at the desk.
      */}
      <RateLimitHelpDesk className="mb-3" />

      <Panel bodyClassName="p-0">
        <DataTable
          rows={rows}
          getKey={(a) => a.id}
          loading={accounts.isLoading}
          empty="No staff accounts yet."
          columns={[
            {
              key: 'username',
              label: 'User ID',
              mobile: 'title',
              /*
                No `whitespace-nowrap` here: a booth manager's user id is their
                work e-mail address, and 30 unbreakable characters pushed the
                table wider than a phone. `break-all` because an address has no
                spaces to wrap at.
              */
              render: (a) => (
                <>
                  <span className="block break-all font-semibold text-[#0B1E3F]">{a.username}</span>
                  {/* The role rides along with the name on a phone, where it
                      has no column of its own. */}
                  <span className="mt-1 block md:hidden">
                    <Chip tone={a.role === 'admin' ? 'info' : 'neutral'}>{ROLE_LABEL[a.role]}</Chip>
                  </span>
                </>
              ),
            },
            {
              key: 'role',
              label: 'Role',
              mobile: 'hide',
              className: 'whitespace-nowrap',
              render: (a) => (
                <Chip tone={a.role === 'admin' ? 'info' : 'neutral'}>{ROLE_LABEL[a.role]}</Chip>
              ),
            },
            {
              key: 'assigned',
              label: 'Assigned to',
              className: 'max-w-64 whitespace-normal break-words text-slate-500',
              render: (a) => a.assignedTo,
            },
            {
              key: 'status',
              label: 'Status',
              className: 'whitespace-nowrap',
              render: (a) => (
                <>
                  {a.locked ? (
                    <Chip tone="critical">
                      Locked
                      {a.lockedUntil &&
                        ` till ${new Date(a.lockedUntil).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`}
                    </Chip>
                  ) : a.mustChangePassword ? (
                    <Chip tone="attention">Temporary password</Chip>
                  ) : (
                    <Chip tone="positive">Active</Chip>
                  )}
                  {a.failedAttempts > 0 && !a.locked && (
                    <span className="ml-1.5 text-xs text-slate-400">{a.failedAttempts} failed</span>
                  )}
                </>
              ),
            },
            {
              key: 'lastLogin',
              label: 'Last sign-in',
              className: 'whitespace-nowrap text-slate-400',
              render: (a) =>
                a.lastLoginAt
                  ? new Date(a.lastLoginAt).toLocaleString([], {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'never',
            },
            {
              key: 'actions',
              label: '',
              mobile: 'actions',
              align: 'right',
              className: 'whitespace-nowrap',
              render: (a) => (
                <span className="flex justify-end gap-1.5">
                  {a.locked && (
                    <AButton
                      size="sm"
                      tone="outline"
                      disabled={unlock.isPending}
                      onClick={() => {
                        setError(null)
                        unlock.mutate(a.id)
                      }}
                    >
                      <LockOpen className="size-3.5" strokeWidth={2.2} />
                      Unlock
                    </AButton>
                  )}
                  <AButton
                    size="sm"
                    tone="outline"
                    onClick={() => {
                      setError(null)
                      setConfirmReset(a)
                    }}
                  >
                    <KeyRound className="size-3.5" strokeWidth={2.2} />
                    Reset
                  </AButton>
                </span>
              ),
            },
          ]}
        />
      </Panel>

      {/* Resetting locks someone out of their current password, so it asks. */}
      <Dialog
        open={confirmReset != null}
        onOpenChange={(open) => !open && setConfirmReset(null)}
      >
        <DialogContent className="rounded-3xl p-0" showClose={false}>
          <div className="px-6 pb-6 pt-7 text-center">
            <span className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-amber-100 text-amber-700">
              <KeyRound className="size-5" strokeWidth={2.2} />
            </span>
            <DialogTitle className="text-base">
              Issue a new password for {confirmReset?.username}?
            </DialogTitle>
            <DialogDescription className="mt-1.5">
              Their current password stops working straight away. The new one is shown once, on
              this screen — it is stored only as a hash, so it cannot be looked up again.
            </DialogDescription>

            <div className="mt-5 flex gap-2.5">
              <AButton
                tone="outline"
                block
                disabled={reset.isPending}
                onClick={() => setConfirmReset(null)}
                autoFocus
              >
                Cancel
              </AButton>
              <AButton
                tone="navy"
                block
                disabled={reset.isPending}
                onClick={() => confirmReset && reset.mutate(confirmReset.id)}
              >
                {reset.isPending ? 'Issuing…' : 'Issue password'}
              </AButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shown once. Nothing writes it to disk or to the audit log. */}
      <Dialog open={issued != null} onOpenChange={(open) => !open && setIssued(null)}>
        <DialogContent className="rounded-3xl p-0" showClose={false}>
          <div className="px-6 pb-6 pt-7 text-center">
            <DialogTitle className="text-base">New password for {issued?.username}</DialogTitle>
            <DialogDescription className="mt-1.5">
              Pass this to them directly. They must change it at first sign-in.
            </DialogDescription>

            <p
              className={cn(
                'mt-4 select-all rounded-lg border border-slate-200 bg-slate-100 px-4 py-3',
                'font-mono text-xl font-semibold tracking-wider text-[#0B1E3F]',
              )}
            >
              {issued?.password}
            </p>

            <Note className="mt-3 text-left">
              This is the only time it is shown. Close this box and it is gone — the system keeps
              only a hash.
            </Note>

            <AButton block className="mt-4" onClick={() => setIssued(null)}>
              I have noted it down
            </AButton>
          </div>
        </DialogContent>
      </Dialog>
    </Section>
  )
}
